<div class="pt-4 pl-md-4" *ngIf="serverError !== null">
  <h2>Server Error: {{ serverError }}</h2>
</div>

<div *ngIf="(serverError === null) && !connNotFound">
  <nav class="navbar navbar-expand navbar-light bg-light capitan-sub-nav">
    <ul class="navbar-nav">
      <li class="nav-item">
        <a routerLink="/accounting-connections" class="nav-link arrow-link">&#8592;</a>
      </li>
    </ul>

    <ul class="navbar-nav mx-auto">
      <!-- No internal navigation (yet) for accounting connections -->
    </ul>
  </nav>

  <div class="capitan-sub-nav-post-content">
    <div class="floating-alert-container">
      <div class="alert alert-success alert-dismissible" role="alert" *ngIf="successMessage">
        {{ successMessage }}
        <button type="button" class="close" (click)="successMessage = ''">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="alert alert-danger alert-dismissible" role="alert" *ngIf="errorMessage">
        {{ errorMessage }}
        <button type="button" class="close" (click)="errorMessage = ''">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="alert alert-info" role="alert" *ngIf="loadingEdit">
        Saving changes...
      </div>
    </div>

    <div class="pt-3 pl-md-4"
         style="margin-bottom: 300px;">
      <h2 class="mb-3">
        Configure Accounting Connection
        <span *ngIf="loadingConn" class="text-muted"> - Loading...</span>
      </h2>

      <ng-container *ngIf="!loadingConn">
        <div class="alert" role="alert"
             [class.alert-success]="modalResult.status === CallbackResultStatus.success"
             *ngIf="modalResult !== null">
          {{ modalResult.text }}
        </div>

        <div class="row mt-3">

          <div class="col-6 col-lg-4">
            <p class="mb-0">
              Connection Type: <b>{{ getAccountingSoftwareDisplay(conn.accounting_software) }}</b>
            </p>

            <!--suppress TypeScriptValidateTypes -->
            <div class="mt-1" *ngIf="[AccountingSoftwareType.quickbooksOnline, AccountingSoftwareType.xero].includes(conn.accounting_software)">
              <button (click)="handleResyncAccountCodes()"
                      class="btn btn-success">
                Resync revenue accounts
              </button>
            </div>
          </div>

        </div>

        <!--suppress TypeScriptValidateTypes -->
        <p class="mt-3" *ngIf="[AccountingSoftwareType.quickbooksOnline, AccountingSoftwareType.xero].includes(conn.accounting_software)">
          A report will be synced to your integrated accounting software at 3am local time each morning, containing total sales and refunds for each revenue account over the previous day.
        </p>

        <hr>

        <div class="row">
          <div class="col-lg-6 col-md-8">
            <label for="nameInput">Connection Name</label>
            <input id="nameInput" type="text" name="name"
                   class="form-control"
                   [(ngModel)]="conn.name">

            <button *ngIf="originalConnName !== conn.name"
                    class="btn btn-primary mt-2"
                    (click)="saveConn()">
              Save Connection Name
            </button>

            <small class="form-text text-muted">
              This name is for internal organization only, and does not impact the connection.
            </small>
          </div>
        </div>

        <div class="form-text text-danger">{{ errors.name }}</div>

        <div class="mt-3">

          <!--suppress TypeScriptValidateTypes -->
          <ng-container *ngIf="[AccountingSoftwareType.quickbooksOnline, AccountingSoftwareType.xero].includes(conn.accounting_software)">
            <hr>

            <p *ngIf="!hasDepositAccount()" class="text-danger">
              <b>No deposit account configured.</b>
            </p>

            <p *ngIf="hasDepositAccount()">
              Deposit account:
              <b *ngIf="conn.accounting_software === AccountingSoftwareType.quickbooksOnline">
                {{ conn.quickbooks_deposit_account_name }}
              </b>
              <b *ngIf="conn.accounting_software === AccountingSoftwareType.xero">
                <span *ngIf="conn.xero_deposit_account_code">{{ conn.xero_deposit_account_code }} -</span>
                {{ conn.xero_deposit_account_name }}
              </b>
            </p>

            <p *ngIf="conn.accounting_software === AccountingSoftwareType.quickbooksOnline">
              The deposit account is the account which receives deposits from your payment processor. This is required in order to sync data to Quickbooks.
            </p>
            <p *ngIf="conn.accounting_software === AccountingSoftwareType.xero">
              The deposit account is the account which receives deposits from your payment processor. If this is set, the daily invoice created by Capitan will automatically be paid out of the deposit account. If this is empty, then the invoice will be left with the status "Submitted", requiring it to be manually reviewed and paid.
            </p>

            <button (click)="openSetDepositAccountModal()"
                    [disabled]="loadingDepositAccount"
                    [class.disabled]="loadingDepositAccount"
                    class="btn btn-primary">
              {{ hasDepositAccount() ? 'Update' : 'Set' }} deposit account
            </button>
            <button *ngIf="(conn.accounting_software === AccountingSoftwareType.xero) && hasDepositAccount()"
                    (click)="clearDepositAccount()"
                    [disabled]="loadingDepositAccount"
                    [class.disabled]="loadingDepositAccount"
                    class="btn btn-secondary ml-2">
              {{ loadingDepositAccount ? 'Loading...' : 'Clear deposit account' }}
            </button>
          </ng-container>

          <ng-container *ngIf="conn.accounting_software === AccountingSoftwareType.none">
            <hr>

            <p>
              Because this is a manual accounting connection, you can modify your revenue accounts at any time. Setting up revenue accounts will allow you to download reports for Quickbooks Desktop or other accounting software.
            </p>

            <button (click)="openConfigureRevenueAccountsModal()"
                    class="btn btn-primary">
              Configure Revenue Accounts
            </button>
          </ng-container>

          <hr>

          <div class="form-group">
            <label for="paymentProcessorFeePolicySelect">Payment Processor Fee Policy</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <select class="custom-select" id="paymentProcessorFeePolicySelect"
                      [(ngModel)]="conn.payment_processor_fee_policy"
                      (ngModelChange)="saveConn()">
                <option [ngValue]="PaymentProcessorFeePolicy.doNotSubtract">Ignore Fees</option>
                <option [ngValue]="PaymentProcessorFeePolicy.subtractAsSeparateLineItem">Report As Separate Line Items</option>
                <option [ngValue]="PaymentProcessorFeePolicy.sendToSeparateAccount">Send To Separate Account</option>
              </select>
            </div>
            <small class="form-text text-muted">
              This determines how payment processor fees are handled when reporting to accounting software. They can either be ignored or reported as separate (negative) line items. Payment processor fees are only tracked for Stripe payments.
            </small>

            <ng-container *ngIf="conn.payment_processor_fee_policy === PaymentProcessorFeePolicy.sendToSeparateAccount">
              <p *ngIf="!hasPaymentProcessorFeeAccount()" class="mt-2 text-danger">
                <b>No payment processor fee account configured.</b>
              </p>

              <p *ngIf="hasPaymentProcessorFeeAccount()" class="mt-2">
                Payment processor fee account:
                <b>
                  <span *ngIf="conn.payment_processor_fee_account_code !== ''">
                    {{ conn.payment_processor_fee_account_code }} -
                  </span>
                  {{ conn.payment_processor_fee_account_name }}
                </b>
              </p>

              <button (click)="openSetPaymentProcessorFeeAccountModal()"
                    class="btn btn-primary">
              {{ hasPaymentProcessorFeeAccount() ? 'Update' : 'Set' }} payment processor fee account
            </button>
            </ng-container>
          </div>

          <hr>

          <div class="form-group">
            <label for="accountingTimeZoneInput">Accounting Time Zone</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <input class="form-control" type="text"
                     id="accountingTimeZoneInput" name="accounting_time_zone"
                     [(ngModel)]="conn.time_zone"
                     [ngbTypeahead]="handleTimeZoneTypeahead">
            </div>
            <div class="form-text text-danger">{{ accountingTimeZoneErrorMessage }}</div>
          </div>

          <p>
            The accounting time zone is used when syncing reports to your accounting software. If this field is left blank, your organization's default time zone, <b>{{ loadingOrgConfig ? '' : orgConfig.time_zone }}</b>, will be used.
          </p>

          <div *ngIf="accountingTimeZoneSuccessMessage" class="alert alert-success">
            {{ accountingTimeZoneSuccessMessage }}
          </div>

          <button (click)="updateAccountingTimeZone()"
                  [disabled]="loadingAccountingTimeZone"
                  [class.disabled]="loadingAccountingTimeZone"
                  class="btn btn-primary">
            {{ loadingAccountingTimeZone ? 'Loading...' : 'Set Accounting Time Zone' }}
          </button>

          <hr>

          <div class="form-group">
            <label for="defaultMembershipRevenueAccountCodeSelect">Default Membership Revenue Account</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <select class="custom-select" id="defaultMembershipRevenueAccountCodeSelect"
                      [(ngModel)]="conn.default_membership_revenue_account_code"
                      (ngModelChange)="saveConn()">
                <option *ngIf="isUnknownRevenueAccountCode(conn.default_membership_revenue_account_code)"
                        [ngValue]="conn.default_membership_revenue_account_code">
                  {{ conn.default_membership_revenue_account_code }} (Account not found)
                </option>
                <option [ngValue]="''">Do Not Report</option>
                <option *ngFor="let revenueAcct of conn.revenue_accounts"
                        [ngValue]="revenueAcct.account_id">
                  {{ getRevenueAccountDisplay(revenueAcct) }}
                </option>
              </select>
            </div>
            <small class="form-text text-muted">
              This is the revenue account to which all membership sales will be sent by default.
            </small>
          </div>

          <label>Specific Membership Revenue Accounts</label>

          <input class="form-control mb-2" type="text"
                 placeholder="Filter by membership name, membership type"
                 [(ngModel)]="membershipFilter">

          <div class="rounded-table-wrapper table-scrollable">
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Membership Name</th>
                <th>Membership Type</th>
                <th>Fee Type</th>
                <th>Revenue Account</th>
              </tr>
              </thead>
              <tbody>
              <ng-container *ngFor="let membership of getFilteredMemberships()">
                <tr>
                  <td>{{ membership.name }}</td>
                  <td>{{ getMembershipTypeString(membership) }}</td>
                  <td>Membership Fee</td>
                  <td class="table-button-cell">
                    <select class="custom-select custom-select-sm"
                            [(ngModel)]="membership.revenue_account_codes[conn.id.toString()]"
                            (ngModelChange)="updateMembershipRevenueAccount(membership)">
                      <option *ngIf="isUnknownRevenueAccountCode(membership.revenue_account_codes[conn.id.toString()])"
                              [ngValue]="membership.revenue_account_codes[conn.id.toString()]">
                        {{ membership.revenue_account_codes[conn.id.toString()] }} (Account not found)
                      </option>
                      <option [ngValue]="''">
                        Default: {{ getDefaultRevenueAccountDisplay(conn.default_membership_revenue_account_code) }}
                      </option>
                      <option [ngValue]="null">Do Not Report</option>
                      <option *ngFor="let revenueAcct of conn.revenue_accounts"
                              [ngValue]="revenueAcct.account_id">
                        {{ getRevenueAccountDisplay(revenueAcct) }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr *ngFor="let addOn of membership.per_customer_add_ons">
                  <td>{{ membership.name }}</td>
                  <td>{{ getMembershipTypeString(membership) }}</td>
                  <td>Add-On Fee &ndash; {{ addOn.name }}</td>
                  <td class="table-button-cell">
                    <select class="custom-select custom-select-sm"
                            [(ngModel)]="addOn.revenue_account_codes[conn.id.toString()]"
                            (ngModelChange)="updateMembershipAddOnRevenueAccount(membership, addOn)">
                      <option *ngIf="isUnknownRevenueAccountCode(addOn.revenue_account_codes[conn.id.toString()])"
                              [ngValue]="addOn.revenue_account_codes[conn.id.toString()]">
                        {{ addOn.revenue_account_codes[conn.id.toString()] }} (Account not found)
                      </option>
                      <option [ngValue]="''">
                        Default: {{ getDefaultRevenueAccountDisplay(conn.default_membership_revenue_account_code) }}
                      </option>
                      <option [ngValue]="null">Do Not Report</option>
                      <option *ngFor="let revenueAcct of conn.revenue_accounts"
                              [ngValue]="revenueAcct.account_id">
                        {{ getRevenueAccountDisplay(revenueAcct) }}
                      </option>
                    </select>
                  </td>
                </tr>
              </ng-container>
              <tr *ngIf="loadingMemberships">
                <td colspan="99">Loading...</td>
              </tr>
              <tr *ngIf="!loadingMemberships && (getFilteredMemberships().length === 0)">
                <td colspan="99">No memberships found</td>
              </tr>
              </tbody>
            </table>
          </div>

          <hr>

          <div class="form-group">
            <label for="defaultEntryPassRevenueAccountCodeSelect">Default Entry Pass Revenue Account</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <select class="custom-select" id="defaultEntryPassRevenueAccountCodeSelect"
                      [(ngModel)]="conn.default_entry_pass_revenue_account_code"
                      (ngModelChange)="saveConn()">
                <option *ngIf="isUnknownRevenueAccountCode(conn.default_entry_pass_revenue_account_code)"
                        [ngValue]="conn.default_entry_pass_revenue_account_code">
                  {{ conn.default_entry_pass_revenue_account_code }} (Account not found)
                </option>
                <option [ngValue]="''">Do Not Report</option>
                <option *ngFor="let revenueAcct of conn.revenue_accounts"
                        [ngValue]="revenueAcct.account_id">
                  {{ getRevenueAccountDisplay(revenueAcct) }}
                </option>
              </select>
            </div>
            <small class="form-text text-muted">
              This is the revenue account to which all entry pass sales will be sent by default.
            </small>
          </div>

          <label>Specific Entry Pass Revenue Accounts</label>

          <input class="form-control mb-2" type="text" placeholder="Filter by name, number of entries"
                 [(ngModel)]="entryPassFilter">

          <div class="rounded-table-wrapper table-scrollable">
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Name</th>
                <th>Number of Entries</th>
                <th>Revenue Account</th>
              </tr>
              </thead>
              <tbody>
              <tr *ngFor="let entryPass of getFilteredEntryPasses()">
                <td>{{ entryPass.name }}</td>
                <td>{{ entryPass.num_passes }}</td>
                <td class="table-button-cell">
                  <select class="custom-select custom-select-sm"
                          [(ngModel)]="entryPass.revenue_account_codes[conn.id.toString()]"
                          (ngModelChange)="updateEntryPassRevenueAccount(entryPass)">
                    <option *ngIf="isUnknownRevenueAccountCode(entryPass.revenue_account_codes[conn.id.toString()])"
                            [ngValue]="entryPass.revenue_account_codes[conn.id.toString()]">
                      {{ entryPass.revenue_account_codes[conn.id.toString()] }} (Account not found)
                    </option>
                    <option [ngValue]="''">
                      Default: {{ getDefaultRevenueAccountDisplay(conn.default_entry_pass_revenue_account_code) }}
                    </option>
                    <option [ngValue]="null">Do Not Report</option>
                    <option *ngFor="let revenueAcct of conn.revenue_accounts"
                            [ngValue]="revenueAcct.account_id">
                      {{ getRevenueAccountDisplay(revenueAcct) }}
                    </option>
                  </select>
                </td>
              </tr>
              <tr *ngIf="loadingEntryPasses">
                <td colspan="99">Loading...</td>
              </tr>
              <tr *ngIf="!loadingEntryPasses && (getFilteredEntryPasses().length === 0)">
                <td colspan="99">No entry passes found</td>
              </tr>
              </tbody>
            </table>
          </div>

          <hr>

          <div class="form-group">
            <label for="defaultEventRevenueAccountCodeSelect">Default Event Revenue Account</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <select class="custom-select" id="defaultEventRevenueAccountCodeSelect"
                      [(ngModel)]="conn.default_event_revenue_account_code"
                      (ngModelChange)="saveConn()">
                <option *ngIf="isUnknownRevenueAccountCode(conn.default_event_revenue_account_code)"
                        [ngValue]="conn.default_event_revenue_account_code">
                  {{ conn.default_event_revenue_account_code }} (Account not found)
                </option>
                <option [ngValue]="''">Do Not Report</option>
                <option *ngFor="let revenueAcct of conn.revenue_accounts"
                        [ngValue]="revenueAcct.account_id">
                  {{ getRevenueAccountDisplay(revenueAcct) }}
                </option>
              </select>
            </div>
            <small class="form-text text-muted">
              This is the revenue account to which all event booking sales will be sent by default.
            </small>
          </div>

          <label>Specific Event Fee Revenue Accounts</label>

          <input class="form-control mb-2" type="text" placeholder="Filter by event type and matrix name"
                 [(ngModel)]="eventPricingMatrixFilter">

          <div class="rounded-table-wrapper table-scrollable">
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Event Type</th>
                <th>Matrix</th>
                <th>Fee Type</th>
                <th>Fee Name</th>
                <th>Revenue Account</th>
              </tr>
              </thead>
              <tbody>
              <ng-container *ngFor="let pricingMatrixWithEventType of getFilteredEventPricingMatrices()">
                <tr *ngFor="let fee of pricingMatrixWithEventType.pricingMatrix.per_participant_fees">
                  <td>{{ pricingMatrixWithEventType.eventTypeName }}</td>
                  <td>{{ pricingMatrixWithEventType.pricingMatrix.name }}</td>
                  <td>Per-Participant Fee</td>
                  <td>{{ fee.name }}</td>
                  <td class="table-button-cell">
                    <select class="custom-select custom-select-sm"
                            [(ngModel)]="fee.revenue_account_codes[conn.id.toString()]"
                            (ngModelChange)="updatePricingMatrixRevenueAccount(pricingMatrixWithEventType.pricingMatrix)">
                      <option *ngIf="isUnknownRevenueAccountCode(fee.revenue_account_codes[conn.id.toString()])"
                              [ngValue]="fee.revenue_account_codes[conn.id.toString()]">
                        {{ fee.revenue_account_codes[conn.id.toString()] }} (Account not found)
                      </option>
                      <option [ngValue]="''">
                        Default: {{ getDefaultRevenueAccountDisplay(conn.default_event_revenue_account_code) }}
                      </option>
                      <option [ngValue]="null">Do Not Report</option>
                      <option *ngFor="let revenueAcct of conn.revenue_accounts"
                              [ngValue]="revenueAcct.account_id">
                        {{ getRevenueAccountDisplay(revenueAcct) }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr *ngFor="let fee of pricingMatrixWithEventType.pricingMatrix.per_booking_fees">
                  <td>{{ pricingMatrixWithEventType.eventTypeName }}</td>
                  <td>{{ pricingMatrixWithEventType.pricingMatrix.name }}</td>
                  <td>Per-Booking Fee</td>
                  <td>{{ fee.name }}</td>
                  <td class="table-button-cell">
                    <select class="custom-select custom-select-sm"
                            [(ngModel)]="fee.revenue_account_codes[conn.id.toString()]"
                            (ngModelChange)="updatePricingMatrixRevenueAccount(pricingMatrixWithEventType.pricingMatrix)">
                      <option *ngIf="isUnknownRevenueAccountCode(fee.revenue_account_codes[conn.id.toString()])"
                              [ngValue]="fee.revenue_account_codes[conn.id.toString()]">
                        {{ fee.revenue_account_codes[conn.id.toString()] }} (Account not found)
                      </option>
                      <option [ngValue]="''">
                        Default: {{ getDefaultRevenueAccountDisplay(conn.default_event_revenue_account_code) }}
                      </option>
                      <option [ngValue]="null">Do Not Report</option>
                      <option *ngFor="let revenueAcct of conn.revenue_accounts"
                              [ngValue]="revenueAcct.account_id">
                        {{ getRevenueAccountDisplay(revenueAcct) }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr *ngFor="let fee of pricingMatrixWithEventType.pricingMatrix.per_participant_add_ons">
                  <td>{{ pricingMatrixWithEventType.eventTypeName }}</td>
                  <td>{{ pricingMatrixWithEventType.pricingMatrix.name }}</td>
                  <td>Per-Participant Add-On</td>
                  <td>{{ fee.name }}</td>
                  <td class="table-button-cell">
                    <select class="custom-select custom-select-sm"
                            [(ngModel)]="fee.revenue_account_codes[conn.id.toString()]"
                            (ngModelChange)="updatePricingMatrixRevenueAccount(pricingMatrixWithEventType.pricingMatrix)">
                      <option *ngIf="isUnknownRevenueAccountCode(fee.revenue_account_codes[conn.id.toString()])"
                              [ngValue]="fee.revenue_account_codes[conn.id.toString()]">
                        {{ fee.revenue_account_codes[conn.id.toString()] }} (Account not found)
                      </option>
                      <option [ngValue]="''">
                        Default: {{ getDefaultRevenueAccountDisplay(conn.default_event_revenue_account_code) }}
                      </option>
                      <option [ngValue]="null">Do Not Report</option>
                      <option *ngFor="let revenueAcct of conn.revenue_accounts"
                              [ngValue]="revenueAcct.account_id">
                        {{ getRevenueAccountDisplay(revenueAcct) }}
                      </option>
                    </select>
                  </td>
                </tr>
                <tr *ngFor="let fee of pricingMatrixWithEventType.pricingMatrix.per_booking_add_ons">
                  <td>{{ pricingMatrixWithEventType.eventTypeName }}</td>
                  <td>{{ pricingMatrixWithEventType.pricingMatrix.name }}</td>
                  <td>Per-Booking Add-On</td>
                  <td>{{ fee.name }}</td>
                  <td class="table-button-cell">
                    <select class="custom-select custom-select-sm"
                            [(ngModel)]="fee.revenue_account_codes[conn.id.toString()]"
                            (ngModelChange)="updatePricingMatrixRevenueAccount(pricingMatrixWithEventType.pricingMatrix)">
                      <option *ngIf="isUnknownRevenueAccountCode(fee.revenue_account_codes[conn.id.toString()])"
                              [ngValue]="fee.revenue_account_codes[conn.id.toString()]">
                        {{ fee.revenue_account_codes[conn.id.toString()] }} (Account not found)
                      </option>
                      <option [ngValue]="''">
                        Default: {{ getDefaultRevenueAccountDisplay(conn.default_event_revenue_account_code) }}
                      </option>
                      <option [ngValue]="null">Do Not Report</option>
                      <option *ngFor="let revenueAcct of conn.revenue_accounts"
                              [ngValue]="revenueAcct.account_id">
                        {{ getRevenueAccountDisplay(revenueAcct) }}
                      </option>
                    </select>
                  </td>
                </tr>
              </ng-container>
              <tr *ngIf="loadingEventPricingMatrices">
                <td colspan="99">Loading...</td>
              </tr>
              <tr *ngIf="!loadingEventPricingMatrices && (getFilteredEventPricingMatrices().length === 0)">
                <td colspan="99">No event pricing matrices found</td>
              </tr>
              </tbody>
            </table>
          </div>

          <hr>

          <div class="form-group">
            <label for="defaultVoucherPackRevenueAccountCodeSelect">Default Voucher Pack Revenue Account</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <select class="custom-select" id="defaultVoucherPackRevenueAccountCodeSelect"
                      [(ngModel)]="conn.default_voucher_pack_revenue_account_code"
                      (ngModelChange)="saveConn()">
                <option *ngIf="isUnknownRevenueAccountCode(conn.default_voucher_pack_revenue_account_code)"
                        [ngValue]="conn.default_voucher_pack_revenue_account_code">
                  {{ conn.default_voucher_pack_revenue_account_code }} (Account not found)
                </option>
                <option [ngValue]="''">Do Not Report</option>
                <option *ngFor="let revenueAcct of conn.revenue_accounts"
                        [ngValue]="revenueAcct.account_id">
                  {{ getRevenueAccountDisplay(revenueAcct) }}
                </option>
              </select>
            </div>
            <small class="form-text text-muted">
              This is the revenue account to which all voucher pack sales will be sent by default.
            </small>
          </div>

          <label>Specific Voucher Pack Revenue Accounts</label>

          <input class="form-control mb-2" type="text" placeholder="Filter by name, number of vouchers"
                 [(ngModel)]="entryPassFilter">

          <div class="rounded-table-wrapper table-scrollable">
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Name</th>
                <th>Number of Vouchers</th>
                <th>Revenue Account</th>
              </tr>
              </thead>
              <tbody>
              <tr *ngFor="let voucherPack of getFilteredVoucherPacks()">
                <td>{{ voucherPack.name }}</td>
                <td>{{ voucherPack.number_of_vouchers }}</td>
                <td class="table-button-cell">
                  <select class="custom-select custom-select-sm"
                          [(ngModel)]="voucherPack.revenue_account_codes[conn.id.toString()]"
                          (ngModelChange)="updateVoucherPackRevenueAccount(voucherPack)">
                    <option *ngIf="isUnknownRevenueAccountCode(voucherPack.revenue_account_codes[conn.id.toString()])"
                            [ngValue]="voucherPack.revenue_account_codes[conn.id.toString()]">
                      {{ voucherPack.revenue_account_codes[conn.id.toString()] }} (Account not found)
                    </option>
                    <option [ngValue]="''">
                      Default: {{ getDefaultRevenueAccountDisplay(conn.default_voucher_pack_revenue_account_code) }}
                    </option>
                    <option [ngValue]="null">Do Not Report</option>
                    <option *ngFor="let revenueAcct of conn.revenue_accounts"
                            [ngValue]="revenueAcct.account_id">
                      {{ getRevenueAccountDisplay(revenueAcct) }}
                    </option>
                  </select>
                </td>
              </tr>
              <tr *ngIf="loadingVoucherPacks">
                <td colspan="99">Loading...</td>
              </tr>
              <tr *ngIf="!loadingVoucherPacks && (getFilteredVoucherPacks().length === 0)">
                <td colspan="99">No voucher packs found</td>
              </tr>
              </tbody>
            </table>
          </div>

          <hr>

          <div class="form-group">
            <label for="gift_card_revenue_account_code">Gift Card Revenue Account</label>
            <div class="row col-md-6 col-lg-4 col-xl-3">
              <select class="custom-select"
                      id="gift_card_revenue_account_code"
                      name="gift_card_revenue_account_code"
                      [(ngModel)]="conn.gift_card_revenue_account_code"
                      (ngModelChange)="saveConn()">
                <option *ngIf="isUnknownRevenueAccountCode(conn.gift_card_revenue_account_code)"
                        [ngValue]="conn.gift_card_revenue_account_code">
                  {{ conn.gift_card_revenue_account_code }} (Account not found)
                </option>
                <option [ngValue]="''">Do Not Report</option>
                <option *ngFor="let revenueAcct of conn.revenue_accounts"
                        [ngValue]="revenueAcct.account_id">
                  {{ getRevenueAccountDisplay(revenueAcct) }}
                </option>
              </select>
            </div>
            <small class="form-text text-muted">
              This is the revenue account to which all gift card sales will be sent.
            </small>
          </div>

        </div>
      </ng-container>
    </div>
  </div>
</div>
